<template>
  <div>
    <form @submit.prevent="handleSubmit" id="user_create_form">

      <!-- Email Address -->
      <div class="mb-4 mt-2">
        <label class="form-label form-label-thin">Email Address <span class="text-danger">*</span></label>
        <input
          type="email"
          class="form-control form-control-thin"
          :class="{
            'border-danger': emailStatus === 'same_company',
            'border-warning': emailStatus === 'other_company',
            'border-success': emailStatus === 'available'
          }"
          id="email"
          name="email"
          ref="email"
          v-model="form.email"
          placeholder="Email Address"
          @input="onEmailInput"
        />

        <!-- Checking indicator -->
        <div v-if="emailChecking" class="mt-1" style="font-size:12px;color:#868e96;">
          <i class="fa fa-spinner fa-spin"></i> Checking email...
        </div>

        <!-- Already in THIS company -->
        <div v-else-if="emailStatus === 'same_company'" class="d-flex align-items-center justify-content-between mt-1">
          <span style="font-size:12px;color:#c92a2a;">
            <i class="fa fa-times-circle"></i> Email is already registered in this company
          </span>
        </div>

        <!-- Exists but in OTHER company -->
        <div v-else-if="emailStatus === 'other_company'" class="d-flex align-items-center justify-content-between mt-1">
          <span style="font-size:12px;color:#c92a2a;">
            <i class="fa fa-exclamation-circle"></i> Email is already registered
          </span>
          <a
            href="#"
            class="text-primary"
            style="font-size:12px;text-decoration:none;white-space:nowrap;"
            @click.prevent="openUserStatusModal"
          >
            View/Take Action
          </a>
        </div>

        <!-- Available -->
        <div v-else-if="emailStatus === 'available'" class="mt-1" style="font-size:12px;color:#099268;">
          <i class="fa fa-check-circle"></i> Email is available
        </div>
      </div>

      <!-- First Name -->
      <div class="mb-4">
        <label class="form-label form-label-thin">First Name <span class="text-danger">*</span></label>
        <input type="text" class="form-control form-control-thin" id="first_name" name="first_name" ref="first_name"
          v-model="form.first_name" placeholder="First Name" />
      </div>

      <!-- Last Name -->
      <div class="mb-4">
        <label class="form-label form-label-thin">Last Name <span class="text-danger">*</span></label>
        <input type="text" class="form-control form-control-thin" id="last_name" name="last_name" ref="last_name"
          v-model="form.last_name" placeholder="Last Name" />
      </div>

      <!-- Phone -->
      <div class="mb-4">
        <label class="form-label form-label-thin">Phone</label>
        <input type="text" class="form-control form-control-thin" id="phone" name="phone" ref="phone"
          v-model="form.phone" placeholder="Phone Number" />
      </div>

      <div class="mb-4">
        <MultiSelect
          v-model="form.role"
          :options="roles"
          option-label="name"
          option-value="id"
          :multiple="false"
          label="User Role"
          placeholder="Select Role"
        />
      </div>

      <!-- Address -->
      <div class="mb-4">
        <label class="form-label form-label-thin">Address</label>
        <textarea class="form-control form-control-thin" id="address" name="address" ref="address"
          v-model="form.address" placeholder="Full Address" rows="2"></textarea>
      </div>

      <!-- Modal Footer -->
      <div class="d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-sm btn-outline-secondary hover-white" @click="close">
          <i class="fa fa-times"></i> Cancel
        </button>
        <button
          type="submit"
          class="btn btn-primary py-1 px-2 text-light hover-white btn-sm form-submit-btn"
          :disabled="submitting || emailStatus === 'same_company'"
        >
          <i class="fa-solid fa-plus fs-12"></i> Create
        </button>
      </div>
    </form>

    <!-- ── User Status Modal ── -->
    <teleport to="body">
      <div
        v-if="showStatusModal"
        class="modal-backdrop-custom"
        @click.self="showStatusModal = false"
      >
        <div class="status-modal">
          <div class="status-modal-header">
            <h6 class="mb-0" style="font-weight:600;">User Status</h6>
          </div>
          <div class="status-modal-body">
            <div class="mb-3">
              <div style="font-size:15px;font-weight:500;color:#212529;">
                {{ existingUser.first_name }} {{ existingUser.last_name }}
              </div>
              <div style="font-size:13px;color:#868e96;">{{ existingUser.email }}</div>
            </div>
            <hr style="margin:12px 0;" />
            <p style="font-size:13px;color:#c92a2a;margin:0;">
              <strong>Note:</strong> User is already registered in other companies.
              You can still add them to your company.
            </p>
          </div>
          <div class="status-modal-footer">
            <button class="btn btn-sm btn-outline-secondary" @click="showStatusModal = false">
              Close
            </button>
            <button
              class="btn btn-primary btn-sm"
              :disabled="addingUser"
              @click="addExistingUser"
            >
              <i class="fa fa-user-plus me-1"></i>
              {{ addingUser ? 'Adding...' : 'Click to Add User' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import MultiSelect from '@/components/MultiSelect.vue'

export default {
  name: 'CreateUser',
  emits: ['created', 'close'],
  components: { MultiSelect },

  props: {
    roles: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      // ── existing ──
      roles: [],
      form: {
        first_name: '',
        last_name:  '',
        email:      '',
        role:       '',
        phone:      '',
        address:    '',
        id:         ''
      },
      submitting: false,
      loading:    false,
      emailChecking:   false,
      emailStatus:     null,  
      existingUser:    {},
      emailDebounce:   null,
      showStatusModal: false,
      addingUser:      false,
    }
  },

  mounted() {
    this.fetchRoles()
  },

  methods: {
    async handleSubmit() {
      // Block submit if email belongs to this company already
      if (this.emailStatus === 'same_company') return

      this.submitting = true
      try {
        const payload = {
          first_name: this.form.first_name,
          last_name:  this.form.last_name,
          email:      this.form.email,
          role:       this.form.role    || null,
          phone:      this.form.phone   || null,
          address:    this.form.address || null,
        }

        const result = await form_api_submit({
          url:          'auth/register',
          method:       'POST',
          data:         payload,
          curr_form_id: '#user_create_form',
        })

        if (result.success) {
          this.$emit('created')
          this.close()
          this.resetForm()
        }
      } finally {
        this.submitting = false
      }
    },

    async fetchRoles() {
      this.loading = true
      try {
        const result = await form_api_submit({
          url:                'roles/get-roles',
          method:             'GET',
          show_success_toast: false,
          show_error_toast:   true,
        })
        if (result.success) {
          this.roles = result.data || []
        }
      } finally {
        this.loading = false
      }
    },

    close() {
      this.$emit('close')
    },

    resetForm() {
      this.form = {
        first_name: '',
        last_name:  '',
        email:      '',
        role:       '',
        phone:      '',
        address:    '',
        id:         ''
      }
      this.emailStatus  = null
      this.existingUser = {}
    },
    onEmailInput() {
      clearTimeout(this.emailDebounce)
      this.emailStatus  = null
      this.existingUser = {}

      const email = this.form.email.trim()
      if (!email || !this.isValidEmail(email)) return

      this.emailDebounce = setTimeout(() => this.checkEmail(email), 500)
    },

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },

    async checkEmail(email) {
      this.emailChecking = true
      try {
        const result = await form_api_submit({
          url:                'user/check-email',
          method:             'POST',
          data:               { email },
          show_success_toast: false,
          show_error_toast:   false,
          remove_pre_toasts:  false,
        })

        if (!result.success) {
          this.emailStatus = null
          return
        }

        const { status, user } = result.data
        this.emailStatus  = status   // 'available' | 'same_company' | 'other_company'
        this.existingUser = user || {}
      } finally {
        this.emailChecking = false
      }
    },

    openUserStatusModal() {
      this.showStatusModal = true
    },

    async addExistingUser() {
      this.addingUser = true
      try {
        const result = await form_api_submit({
          url:                'user/add-to-company',
          method:             'POST',
          data:               {
            user_id: this.existingUser.id,
            role_id: this.form.role || null,
          },
          show_success_toast: true,
          show_error_toast:   true,
          remove_pre_toasts:  false,
        })

        if (result.success) {
          this.showStatusModal = false
          this.$emit('created')
          this.close()
          this.resetForm()
        }
      } finally {
        this.addingUser = false
      }
    },
  },
}
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
}
.status-modal {
  background: #fff;
  border-radius: 12px;
  width: 480px;
  max-width: calc(100vw - 32px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}
.status-modal-header {
  padding: 18px 24px 14px;
  border-bottom: 1px solid #f1f3f5;
}
.status-modal-body {
  padding: 20px 24px;
}
.status-modal-footer {
  padding: 14px 24px 18px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid #f1f3f5;
}
</style>